// Microsoft Clarity dentro de la app (UD-7 de /ux, 18/10/2026). Quién se graba lo decide
// `clarityAudience`; esto sólo carga el tag y le habla. Lo que se manda son IDS y nombres fijos,
// NUNCA un mail ni un nombre: Clarity guarda el `identify` hasheado, pero el nombre amigable y las
// etiquetas se ven tal cual en el panel.
//
// Lo que se ve de la pantalla lo decide el modo del proyecto (estricto, en el panel de Clarity) y el
// `data-clarity-mask` del `<body>` (`public/index.html`), que tapa todo aunque alguien cambie el modo.
// Se destapa sólo texto de Dashbo, elemento por elemento, con `CLARITY_UNMASK` (`./unmask.js`).

// ⚠️ El `<script>` NO se puede llamar `clarity`: el navegador expone todo elemento con id como
// `window.<id>`, y con `id="clarity"` el global era el `<script>` y el tag moría con
// `a[c] is not a function` (le pasó al sitio del 09 al 15/10, `dashbo-sitio/CLAUDE.md`).
export const CLARITY_TAG_ELEMENT_ID = 'dashbo-clarity-tag';

const IDLE = 'idle';
const RECORDING = 'recording';
const STOPPED = 'stopped';

function ensureQueue() {
    if (typeof window.clarity === 'function') return;
    window.clarity = function () {
        (window.clarity.q = window.clarity.q || []).push(arguments);
    };
}

function injectTag(projectId) {
    ensureQueue();
    const script = document.createElement('script');
    script.async = true;
    script.id = CLARITY_TAG_ELEMENT_ID;
    script.src = `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`;
    document.head.appendChild(script);
}

function blockRestart() {
    try {
        Object.defineProperty(window.navigator, 'globalPrivacyControl', {value: true, configurable: true});
    } catch (error) {
        // Un navegador que no deja definirlo: queda sólo el `stop`.
    }
}

class ClarityRecorderImpl {
    constructor() {
        this._state = IDLE;
    }

    isRecording() {
        return this._state === RECORDING;
    }

    // Carga el tag una sola vez por carga de página. Una vez detenido no vuelve a arrancar: la
    // próxima carga decide de nuevo. Devuelve si está grabando.
    start(projectId) {
        if (this._state === IDLE && projectId && typeof window !== 'undefined') {
            injectTag(projectId);
            this._state = RECORDING;
        }
        return this.isRecording();
    }

    // El nombre amigable es lo que el panel muestra en lugar del hash: los dos ids, nada más.
    identify(userId, agencyId) {
        if (!userId) return;
        const friendlyName = agencyId ? `a${agencyId}-u${userId}` : `u${userId}`;
        this._call('identify', String(userId), null, null, friendlyName);
    }

    tag(key, value) {
        if (value === null || value === undefined || value === '') return;
        this._call('set', key, String(value));
    }

    event(name) {
        if (!name) return;
        this._call('event', name);
    }

    // `stop` no está en la documentación de Clarity pero sí en su tag (`clarity-js/src/clarity.ts`,
    // `window.clarity('stop')`): corta la grabación sin recargar. Sirve para la pestaña donde entra
    // otro usuario sin recargar (cerrar sesión y entrar con un superadmin, o con una agencia paga).
    //
    // ⚠️ Solo no alcanza. En cada cambio de URL el tag se detiene SOLO y vuelve a arrancar a los 250 ms
    // (`core/history.ts`, `RestartDelay`), y nuestro efecto corre justo en esa ventana: el `stop` lo
    // encuentra ya detenido, no hace nada, y el tag re-arranca grabando la pantalla nueva. Por eso,
    // además, se prende `globalPrivacyControl` en esta pestaña: el re-arranque pasa por `core.check()`,
    // que con esa señal no arranca (`core/index.ts`). Es determinístico y dura hasta la próxima carga,
    // que es lo mismo que promete este `stop`. (Al detenerse, el tag sube lo pendiente de la página en
    // la que estaba, que sí se grababa.)
    stop() {
        if (this._state !== RECORDING) return;
        this._call('stop');
        this._state = STOPPED;
        blockRestart();
    }

    _call(...args) {
        if (!this.isRecording() || typeof window === 'undefined' || typeof window.clarity !== 'function') return;
        try {
            window.clarity(...args);
        } catch (error) {
            // Clarity no puede romper la app: una llamada que falla se pierde.
        }
    }

    // Para los tests.
    _reset() {
        this._state = IDLE;
        try {
            delete window.navigator.globalPrivacyControl;
        } catch (error) {
            // nada que limpiar
        }
    }
}

export const ClarityRecorder = new ClarityRecorderImpl();
